import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { SEOHead } from "../components/SEOHead";
import { useTranslations } from "../hooks/useTranslations";
import {
  AUDIO_TRANSLATION_LANDING_PATH,
  buildAudioTranslationPairPath,
  getAudioTranslationLandingContent,
  getAudioTranslationPairButtonLabel,
  getAudioTranslationPairs,
} from "../content/audioTranslation";
import { getLocalizedPath } from "../utils/localizedRoutes";

export function AudioTranslationPage() {
  const navigate = useNavigate();
  const { currentLanguage } = useTranslations();
  const landingContent = getAudioTranslationLandingContent(currentLanguage);
  const pairs = getAudioTranslationPairs();
  const heroTitleStyle = { fontSize: "48px", lineHeight: "0.98" as const };
  const contentSections = landingContent.contentSections;

  return (
    <>
      <SEOHead
        overrideSeo={{
          title: landingContent.seoTitle,
          description: landingContent.seoDescription,
          keywords:
            "audio translation, spanish audio translation, spanish voice over, spanish to english voice over, spanish to french voice over",
        }}
        canonicalPathOverride={AUDIO_TRANSLATION_LANDING_PATH}
      />
      <Header />
      <main className="min-h-screen vevi-site-background" style={{ paddingTop: "112px" }}>
        <div className="vevi-page-grid">
          <div className="vevi-page-main py-6 sm:py-8 lg:py-10">
            <div className="mb-12">
              <nav
                aria-label="Breadcrumb"
                className="mb-4 flex flex-wrap items-center gap-3 text-sm font-medium text-white"
              >
                <Link
                  to={getLocalizedPath("/", currentLanguage)}
                  className="inline-flex items-center transition-colors hover:text-purple-100"
                >
                  {landingContent.breadcrumbHomeLabel}
                </Link>
                <span className="text-purple-200/80">|</span>
                <span className="text-white">{landingContent.breadcrumbCurrentLabel}</span>
              </nav>

              <h1
                className="mb-4 max-w-5xl font-semibold tracking-tight text-white"
                style={heroTitleStyle}
              >
                {landingContent.title}
              </h1>
              <p className="max-w-4xl text-base leading-relaxed text-purple-100 sm:text-lg">
                {landingContent.description}
              </p>
            </div>

            <Card className="w-full bg-white/95 backdrop-blur-sm shadow-xl">
              <CardContent className="p-8">
                <div className="mb-8 text-center">
                  <h2 className="text-slate-900 text-3xl mb-3">
                    {landingContent.pairsSectionTitle}
                  </h2>
                  <p className="text-slate-600 max-w-3xl mx-auto">
                    {landingContent.pairsSectionDescription}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pairs.map((pair) => (
                    <Button
                      key={pair.pathSlug}
                      asChild
                      className="h-auto min-h-14 whitespace-normal break-words bg-gradient-to-r from-purple-600 to-blue-600 px-5 py-4 text-left hover:from-purple-700 hover:to-blue-700"
                    >
                      <Link to={getLocalizedPath(buildAudioTranslationPairPath(pair), currentLanguage)}>
                        {getAudioTranslationPairButtonLabel(currentLanguage, pair)}
                      </Link>
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="mt-12 grid grid-cols-1 gap-6">
              {contentSections.map((section) => (
                <Card
                  key={section.title}
                  className="bg-white/95 backdrop-blur-sm shadow-xl"
                >
                  <CardContent className="p-8">
                    <h2 className="text-slate-900 text-3xl mb-4">
                      {section.title}
                    </h2>
                    <p className="text-slate-600 leading-7">
                      {section.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        <div className="h-10 sm:h-12 lg:h-14" aria-hidden="true" />

        <Footer
          onShowBlog={() => {
            navigate(getLocalizedPath("/blog", currentLanguage));
          }}
        />
      </main>
    </>
  );
}

export default AudioTranslationPage;
